import { OrderModel } from "../orders/models/order.model";
import { ESC_POS, PrinterConfig, ReceiptItem } from "./models/printer";

type PrinterWriter = WritableStreamDefaultWriter<Uint8Array>;
type SerialPort = any;

export class PrinterService {
  private static serialPort: SerialPort | null = null;
  private static usbDevice: USBDevice | null = null;

  static async connectSerial(): Promise<PrinterWriter | null> {
    try {
      if (this.isPortOpen()) {
        return this.serialPort.writable?.getWriter() ?? null;
      }
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600 });
      this.serialPort = port;
      return port.writable?.getWriter() ?? null;
    } catch (error) {
      console.error("Serial connection error:", error);
      return null;
    }
  }

  static async disconnectSerial(): Promise<void> {
    try {
      if (this.serialPort) {
        await this.serialPort.close();
        this.serialPort = null;
        console.log("Serial port disconnected");
      }
    } catch (error) {
      console.error("Serial disconnection error:", error);
      throw error;
    }
  }

  static async connectUSB(): Promise<any> {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [{ vendorId: 0x6868 }],
      });
      console.log("device", device);

      await device.open();
      await device.selectConfiguration(1);
      await device.claimInterface(0);
      this.usbDevice = device;
      return device;
    } catch (error) {
      console.error("USB connection error:", error);
      return null;
    }
  }

  static async disconnectUSB(): Promise<void> {
    try {
      if (this.usbDevice) {
        await this.usbDevice.close();
        this.usbDevice = null;
        console.log("USB device disconnected");
      }
    } catch (error) {
      console.error("USB disconnection error:", error);
      throw error;
    }
  }

  static isConnected(): boolean {
    return !!(this.serialPort || this.usbDevice);
  }

  static isPortOpen(): boolean {
    return (
      this.serialPort !== null &&
      this.serialPort.readable !== null &&
      this.serialPort.writable !== null
    );
  }

  static async disconnect(): Promise<void> {
    await Promise.all([this.disconnectSerial(), this.disconnectUSB()]);
  }

  // Helper to format currency
  formatCurrency(amount: number): string {
    return "$" + amount.toFixed(2);
  }

  // Generate ESC/POS commands for OrderModel
  static async printOrderReceipt(
    writer: PrinterWriter,
    order: OrderModel
  ): Promise<void> {
    try {
      const encoder = new TextEncoder();
      const commands: number[] = [];

      // ===== UTILITY FUNCTIONS =====
      const pushCommand = (cmd: number[]) => commands.push(...cmd);
      const pushText = (text: string) => commands.push(...encoder.encode(text));
      const pushLine = (text: string = "") => pushText(text + "\n");

      // ===== PRINTER INITIALIZATION =====
      pushCommand([...ESC_POS.INIT]); // Initialize printer
      pushCommand([...ESC_POS.ALIGN_CENTER]); // Center alignment

      // ===== RECEIPT HEADER =====
      pushCommand([...ESC_POS.TEXT]);
      pushLine("ORDER RECEIPT");
      pushCommand([...ESC_POS.TEXT]);
      pushLine(order.company.name.toUpperCase());
      pushLine("----------------");

      // ===== ORDER DETAILS =====
      pushCommand([...ESC_POS.ALIGN_LEFT]);

      pushLine(`Order #: ${order._id.slice(-8)}`);
      pushLine(`Date: ${new Date(order.createdAt).toLocaleString()}`);
      if (order.customer) {
        pushLine(`Customer: ${order.customer}`);
      }
      pushLine(`Vehicle: ${order.plateNumber}`);
      pushLine(`Status: ${order.status.toUpperCase()}`);
      pushLine(`Payment: ${order.paymentStatus.toUpperCase()}`);
      pushCommand([...ESC_POS.ALIGN_CENTER]); // Center alignment
      pushLine("----------------");
      pushCommand([...ESC_POS.ALIGN_LEFT]); // Center alignment

      // Service Line
      pushCommand([...ESC_POS.BOLD_ON]);
      pushLine(
        `${order.service.name.padEnd(20)} ${formatCurrency(
          order.service.price
        )}`
      );
      pushCommand([...ESC_POS.BOLD_OFF]); // Bold off

      // Add-ons
      if (order.addOns.length > 0) {
        pushCommand([...ESC_POS.ALIGN_CENTER]); // Center alignment
        pushLine("----------------");
        pushCommand([...ESC_POS.ALIGN_LEFT]);

        pushLine("Add-ons:");
        order.addOns.forEach((addon) => {
          pushLine(
            ` - ${addon.name.padEnd(18)} ${formatCurrency(addon.price)}`
          );
        });
      }

      pushCommand([...ESC_POS.ALIGN_CENTER]); // Center alignment
      pushLine("----------------");

      // ===== TOTALS =====
      pushCommand([...ESC_POS.ALIGN_RIGHT]);
      pushCommand([...ESC_POS.TEXT]);
      pushLine(`TOTAL: ${formatCurrency(order.totalCost)}`);
      pushCommand([...ESC_POS.TEXT]);

      // ===== FOOTER =====
      pushCommand([...ESC_POS.ALIGN_CENTER]);
      pushLine("----------------");
      pushLine("Thank you for your business!");
      pushLine("MIKROSELL SYSTEMS");
      pushLine("0797313732");

      pushLine("\n\n");

      // ===== PAPER CUT =====
      pushCommand([...ESC_POS.CUT_PARTIAL]);
      await writer.write(new Uint8Array(commands));
    } catch (error) {
      console.error("Print error:", error);
      throw error;
    }
  }
}

const formatCurrency = (amount: number): string => {
  return "KSH" + amount.toFixed(2);
};
